export type Plan = "free" | "pro";
/** full → captions (free voice off) → original (free translation off) → off (kill switch, everyone). */
export type DegradeMode = "full" | "captions" | "original" | "off";

export const PLAN_LIMITS = {
  free: { voiceSeconds: 150, captionSeconds: 3600, priceUsd: 0, ads: true },
  pro: { voiceSeconds: 12000, captionSeconds: Infinity, priceUsd: 10, ads: false },
} as const;

/**
 * List prices (USD). Soniox bills real-time speech-to-text (+ translation) for the whole time a
 * stream is open, and text-to-speech per hour of generated audio. Chat translation uses a small
 * OpenAI text model.
 */
export const PRICES = {
  sttPerHour: 0.18,
  ttsPerHour: 0.7,
  llmInPerMillionTokens: 0.1,
  llmOutPerMillionTokens: 0.5,
  charsPerToken: 4,
} as const;

/** Absolute monthly ceiling for everything the app costs to run. */
export const HARD_CAP_USD = 99;
/** Reserved for fixed bills: Workers Paid $5 + Supabase Pro $25 + domain $1. */
export const FIXED_COSTS_USD = 31;
/** Metered costs are estimates; count them 10 % high so the real bill stays under the cap. */
export const COST_SAFETY_MARGIN = 1.1;
/** Fractions of the usage budget at which features switch off. */
export const CUTOFFS = { freeVoice: 0.6, freeCaptions: 0.8, killSwitch: 0.97 } as const;

/** Cloudflare list prices and monthly free allowances (Workers Paid). */
export const CLOUDFLARE = {
  egressFreeGb: 1000,
  egressPerGb: 0.05,
  doGbSecondsIncluded: 400_000,
  doPerMillionGbSeconds: 12.5,
  doRequestsIncluded: 1_000_000,
  doPerMillionRequests: 0.15,
} as const;

export interface Usage {
  sttSeconds: number; // seconds Soniox speech streams were open
  ttsSeconds: number; // seconds of translated speech generated
  llmInChars: number;
  llmOutChars: number;
}

export function costUsd(u: Usage): number {
  return (
    (u.sttSeconds / 3600) * PRICES.sttPerHour +
    (u.ttsSeconds / 3600) * PRICES.ttsPerHour +
    (u.llmInChars / PRICES.charsPerToken / 1e6) * PRICES.llmInPerMillionTokens +
    (u.llmOutChars / PRICES.charsPerToken / 1e6) * PRICES.llmOutPerMillionTokens
  );
}

/** Usage budget: what is left of the hard cap after fixed costs. Deliberately does not grow with Pro. */
export function budgetCapUsd(o: { hardCapUsd?: number; fixedUsd?: number } = {}): number {
  return (o.hardCapUsd ?? HARD_CAP_USD) - (o.fixedUsd ?? FIXED_COSTS_USD);
}

export interface InfraUsage {
  egressGb: number;
  doGbSeconds: number;
  doRequests: number;
}

/** Cloudflare usage cost beyond the plan's free allowances. */
export function infraCostUsd(u: InfraUsage): number {
  const c = CLOUDFLARE;
  return (
    Math.max(0, u.egressGb - c.egressFreeGb) * c.egressPerGb +
    (Math.max(0, u.doGbSeconds - c.doGbSecondsIncluded) / 1e6) * c.doPerMillionGbSeconds +
    (Math.max(0, u.doRequests - c.doRequestsIncluded) / 1e6) * c.doPerMillionRequests
  );
}

export function degradeMode(spentUsd: number, capUsd: number, plan: Plan): DegradeMode {
  const used = capUsd > 0 ? spentUsd / capUsd : 1;
  if (used >= CUTOFFS.killSwitch) return "off";
  if (plan === "pro") return "full";
  if (used >= CUTOFFS.freeCaptions) return "original";
  if (used >= CUTOFFS.freeVoice) return "captions";
  return "full";
}
